import React, { useEffect, useState } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useForm } from "react-hook-form";
import StageTwo from "./StageTwo";
import Link from "next/link";
import {
  ChevronRightGrey,
  NewLogo,
  TickCyan,
  OnboardSummary,
} from "@/components/bnp-icons";
import { Button } from "@/components/bnp-ui";
import { useCompatRouter as useRouter } from "@/lib/compat-router/context";
import { AuthService } from "@/services";
import parseHttpError from "@/utils/parseHttpError";
import { useMutation } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { useAuth } from "@/contexts/AuthContext";
import { usePostHogAnalytics } from "@/contexts/PostHogAnalyticsContext";

type TStage = {
  title: string;
  content: React.ReactNode | React.JSX.Element;
};

type FormData = {
  email: string;
  full_name: string;
  role: string;
  user_interest?: string;
  business_name?: string;
  business_address?: string;
  business_credential_link?: string[];
  city?: string;
  state?: string;
  //recaptchaToken: string;
};

export const REALTORS_WHATSAPP_COMMUNITY_URL =
  "https://chat.whatsapp.com/CGK6urto0zlAp4lhuSu8Co?mode=gi_t";
export const DEVELOPER_WHATSAPP_COMMUNITY_URL =
  "https://chat.whatsapp.com/I41NPbQCda2H00wHG3R6SI?mode=gi_t";

const WhatsAppIcon = ({ className }: { className?: string }) => (
  <svg
    className={className}
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="currentColor"
  >
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
  </svg>
);

const Onboarding = () => {
  const router = useRouter();
  const { refreshUser } = useAuth();
  const ph = usePostHogAnalytics();
  const { token, id, role: roleParam } = router.query;
  const email = token ? atob(token as string) : "";
  const full_name = id ? atob(id as string) : "";
  const selectedRole = (roleParam as string) || "";
  const [currentStage, setCurrentStage] = useState(0);
  const [selectedUserInterest, setSelectedUserInterest] = useState<string>("");

  const defaultValues: FormData = {
    email,
    full_name,
    role: selectedRole,
    user_interest: "",
    business_address: "",
    business_name: "",
    business_credential_link: [],
    city: "",
    state: "",
    // recaptchaToken: "",
  };

  const validationSchema = yup.object().shape({
    email: yup.string().email().required("Email is required"),
    full_name: yup.string().required("Full name is required"),
    role: yup.string(),
    user_interest:
      selectedRole === "regular-user"
        ? yup.string().required("User interest is required")
        : yup.string(),
    state: yup.string(),
    city: yup.string(),
    business_address:
      selectedRole === "regular-user"
        ? yup.string()
        : yup.string().required("Business address is required"),
    business_name:
      selectedRole === "regular-user"
        ? yup.string()
        : yup.string().required("Business name is required"),
    business_credential_link: yup.array(),
    /*    
    recaptchaToken: yup
      .string()
      .required("Please complete the reCAPTCHA verification"),
    */
  });

  const {
    control,
    handleSubmit,
    setValue,
    getValues,
    watch,
    formState: { errors, isValid },
  } = useForm<FormData>({
    defaultValues,
    resolver: yupResolver(validationSchema) as any,
    mode: "onChange",
  });

  // Sync role from URL param when it loads
  useEffect(() => {
    if (selectedRole) {
      setValue("role", selectedRole);
    }
  }, [selectedRole, setValue]);

  // Funnel: onboarding form viewed (stage 0). Fires once per mount.
  useEffect(() => {
    ph.trackEvent("onboarding_started", { role: selectedRole || undefined });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const user_interest = watch("user_interest");
  const business_name = watch("business_name");
  const business_address = watch("business_address");
  //const recaptchaToken = watch("recaptchaToken");

  const isStageComplete = (stageIndex: number) => {
    const values = getValues();

    switch (stageIndex) {
      case 0: // More Info
        if (selectedRole === "regular-user") {
          return Boolean(values.user_interest && !errors.user_interest);
        }
        return Boolean(
          values.business_address &&
          !errors.business_address &&
          values.business_name &&
          !errors.business_name,
        );
      case 1:
        return true;
      //case 1: //  return Boolean(values.recaptchaToken && !errors.recaptchaToken);
      default:
        return false;
    }
  };

  const getStageStatus = (index: number) => {
    if (index === currentStage) return "current";
    if (index < currentStage && isStageComplete(index)) return "completed";
    return "pending";
  };

  const stages: TStage[] = [
    {
      title: "More Info",
      content: (
        <StageTwo
          control={control}
          errors={errors}
          setValue={setValue}
          selectedRole={selectedRole}
          selectedUserInterest={selectedUserInterest}
          setSelectedUserInterest={setSelectedUserInterest}
          email={email}
          fullName={full_name}
          values={getValues()}
          subStep={0}
          setSubStep={() => {}}
        />
      ),
    },
    {
      title: "Success",
      content: (
        <div className="flex flex-col items-center w-[100%] lg:w-[392px]">
          <div className="items-center flex flex-col gap-4 w-[100%] border-b border-soft-200 pb-6 mb-6">
            <OnboardSummary />
            <div className="flex flex-col gap-1 items-center">
              <h1 className="text-main-900 font-semibold text-2xl text-center">
                Onboarding Complete
              </h1>
              <p className="text-base text-grey-7 text-center">
                Your onboarding has been submitted successfully.
              </p>
            </div>
          </div>

          {selectedRole !== "regular-user" && (
            <div className="w-[100%] border border-soft-200 rounded-[20px] p-4 mb-6">
              <div className="flex items-center gap-3">
                <WhatsAppIcon className="w-10 h-10 text-[#25D366] shrink-0" />
                <div className="flex flex-col gap-1">
                  <p className="text-sm font-semibold text-main-900">
                    Join our WhatsApp Community
                  </p>
                  <p className="text-xs text-grey-7">
                    Don&apos;t build your business alone. Join the
                    FindNigeriaProperties.com{" "}
                    {selectedRole === "developer" ? "Developers" : "Realtors"}{" "}
                    Community and stay connected with professionals who are
                    growing, learning, and winning together
                  </p>
                </div>
              </div>
              <a
                href={
                  selectedRole === "developer"
                    ? DEVELOPER_WHATSAPP_COMMUNITY_URL
                    : REALTORS_WHATSAPP_COMMUNITY_URL
                }
                target="_blank"
                rel="noopener noreferrer"
                className="mt-4 w-[100%] flex items-center justify-center gap-2 h-[48px] rounded-lg bg-[#25D366] text-white text-sm font-bold hover:opacity-90 transition-opacity"
              >
                <WhatsAppIcon className="w-5 h-5" />
                Join the Community
              </a>
            </div>
          )}

          <Link href="/user/dashboard" className="w-[100%]">
            <Button className="bg-bnp-dark text-white text-sm font-extrabold w-[100%] h-[52px] rounded-lg">
              Proceed To Your Dashboard
            </Button>
          </Link>
        </div>
      ),
    },
  ];

  const onboardRequest = async () => {
    try {
      const values = getValues();
      const payload: any = {
        email,
        full_name,
        recaptchaToken: "", //values.recaptchaToken,
      };

      if (selectedRole === "regular-user") {
        payload.user_interest = values.user_interest;
      } else {
        payload.business_details = {
          business_name: values.business_name,
          business_address: values.business_address,
          business_credential_link: values.business_credential_link,
          city: values.city,
          state: values.state,
        };
        payload.city = values.city;
        payload.state = values.state;
      }

      const response = await AuthService.onboard(payload);
      return response.data;
    } catch (error: any) {
      console.log(error);
      throw new Error(parseHttpError(error).message);
    }
  };

  const mutation: any = useMutation({
    mutationFn: onboardRequest,
    onSuccess: async (res: any) => {
      console.log(res?.data);
      toast.success("Onboarding complete!");
      // complete_signup sets the auth cookies — hydrate the profile now.
      try {
        await refreshUser();
      } catch {
        // Profile fetch failed; onboarding itself succeeded.
      }
      const values = getValues();
      ph.trackEvent("onboarding_completed", {
        role: selectedRole || undefined,
        has_business_info: selectedRole !== "regular-user",
        user_interest:
          selectedRole === "regular-user"
            ? values.user_interest || undefined
            : undefined,
      });
      if (selectedRole === "realtor" || selectedRole === "developer") {
        ph.trackEvent("realtor_onboarding_submitted", {
          role: selectedRole,
          has_business_name: Boolean(values.business_name),
          state: values.state || undefined,
          city: values.city || undefined,
        });
      }
      setCurrentStage(1);
    },
    onError: (error: any) => {
      const msg = error.message || "User onboarding failed";
      // The email was already onboarded, so there is nothing to complete here.
      // Keep the user on this page and show the final Success screen instead.
      if (
        msg.toLowerCase().includes("realtor with this email already exists")
      ) {
        toast.info("The onboarding data already exist");
        setCurrentStage(stages.length - 1);
        return;
      }
      toast.error(msg);
    },
  });

  return (
    <div className="font-sans bg-[url('/assets/images/onboarding-pattern.png')] min-h-[100vh] bg-center object-cover bg-no-repeat w-[100%] overflow-x-hidden">
      <div className="w-[100%] flex flex-col lg:flex-row justify-between lg:items-center bg-bg-white overflow-x-hidden border-b lg:border border-soft-200 pt-5 lg:pt-8 pb-3 px-4 lg:px-12 lg:py-8 h-[94px] lg:h-[94px]">
        <div className="justify-start items-center flex gap-1">
          <NewLogo />
          <p className="text-bnp-dark text-sm font-semibold">
            FindNigeriaProperties.com
          </p>
        </div>

        <div className="w-[100%] flex items-center lg:justify-center gap-6 font-semibold text-sm overflow-x-scroll">
          {stages.map((stage: TStage, index: number) => {
            const status = getStageStatus(index);
            return (
              <div key={index} className="flex items-center gap-2 min-w-max">
                {status === "completed" ? (
                  <div>
                    <TickCyan />
                  </div>
                ) : status === "current" ? (
                  <div className="bg-bnp-dark text-white flex justify-center items-center rounded-full p-[4px] text-center w-[20px] min-w-[20px] min-h-[20px] max-h-[20px]">
                    {index + 1}
                  </div>
                ) : (
                  <div className="border border-soft-200 text-grey-7 flex justify-center items-center rounded-full p-[4px] text-center w-[20px] min-w-[20px] min-h-[20px] max-h-[20px]">
                    {index + 1}
                  </div>
                )}

                <p
                  className={`text-sm ${
                    status === "current" ? "text-main-900" : "text-grey-7"
                  }`}
                >
                  {stage.title}
                </p>

                {index !== stages.length - 1 && <ChevronRightGrey />}
              </div>
            );
          })}
        </div>
      </div>

      <form
        action={"submit"}
        onSubmit={(e) => e.preventDefault()}
        className="pt-8 px-4 pb-3 lg:px-12 lg:py-8 flex flex-col justify-center items-center w-[100%]"
      >
        {stages[currentStage].content}

        {currentStage === 0 && (
          <div className="flex flex-col lg:flex-row gap-4 items-center w-[100%] lg:w-[392px]">
            <Button
              className="bg-bnp-dark text-white text-sm font-extrabold w-[100%] h-[52px] rounded-lg mt-4 lg:mt-12"
              disabled={!isStageComplete(0) || mutation.isPending}
              isLoading={mutation.isPending}
              onClick={() => {
                mutation.mutate();
              }}
            >
              Submit
            </Button>
          </div>
        )}
        {currentStage === 0 && (
          <div className="flex flex-col items-center">
            <Link href={"/"}>
              <p className="text-sm font-medium text-main-900 underline mt-6 text-center">
                Not Interested in Onboarding? Go to Site
              </p>
            </Link>
          </div>
        )}
      </form>
    </div>
  );
};

export default Onboarding;
